import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { SeoInterface, Tracking } from 'common/types/Page'
import { pageEditorHttpClient } from 'src/editor/api/http-clients/page-editor-http-client'
import { websiteHttpClient } from 'src/editor/api/http-clients/website-http-client'
import { GetWebsitePagesResponse } from 'src/editor/store/website-pages/website-pages-types'
import {
  WebsiteCreatePage,
  WebsitePageResource,
} from 'src/shared/types/website-page-interface'
import { WebsitePageSavePayloadInterface } from 'src/shared/types/website-page-save-payload-interface'
import { WebsitePageDataInterface } from 'src/shared/types/website-settings-types'

export const getWebsitePageById = (id: number) =>
  websiteHttpClient<WebsitePageDataInterface>(`website-pages/${id}`, 'GET')

export const getWebsitePages = async (websiteId: number) =>
  websiteHttpClient<GetWebsitePagesResponse>(
    `websites/${websiteId}/pages`,
    'GET',
  )

export interface GetWebsiteTrackingAndSeoResponse {
  id: number
  tracking: Tracking
  seo: SeoSettingsFields
}

export const getWebsiteTrackingAndSeo = async (websiteId: number) =>
  pageEditorHttpClient<GetWebsiteTrackingAndSeoResponse>(
    `page/${websiteId}/seo-and-tracking`,
    'GET',
  )

export interface UpdateGeneralWebsitePageRequest {
  websitePageId: number
  name: string
  url: string
}

export const updateWebsitePageRequest = async ({
  websitePageId,
  name,
  url,
}: UpdateGeneralWebsitePageRequest) =>
  websiteHttpClient<void>(`website-pages/${websitePageId}`, 'PUT', {
    data: {
      path: url,
      name,
    },
  })

export interface SeoSettingsFields extends Omit<SeoInterface, 'socialImage'> {
  title: string
  description: string
  author: string
  keywords: string
  index?: boolean
  socialImage?: number
}

export interface UpdateSeoWebsitePageRequest
  extends Omit<SeoSettingsFields, 'socialImage'> {
  socialImage?: number
  websiteId: number
  websiteLayoutPageId: number
}

export const updateWebsitePageSeo = async ({
  pageContentId,
  ...rest
}: UpdateSeoWebsitePageRequest & { pageContentId: number }) =>
  pageEditorHttpClient<UpdateSeoWebsitePageRequest>(
    `page/${pageContentId}/seo`,
    'PUT',
    {
      data: rest,
    },
  )

export interface UpdateAdvancedWebsitePageRequest extends Tracking {
  pageId: number
  websiteId: number
  websiteLayoutPageId: number
}

export const updateWebsitePageAdvanced = async ({
  pageId,
  ...rest
}: UpdateAdvancedWebsitePageRequest) =>
  pageEditorHttpClient<Tracking>(`page/${pageId}/tracking`, 'PUT', {
    data: rest,
  })

export interface CreateWebsitePageRequest {
  websiteId: number
  pageType: string
  name: string
  url: string
  locale: PageLocaleEnum
}

export const createWebsitePage = async ({
  websiteId,
  pageType,
  name,
  url,
  locale,
}: CreateWebsitePageRequest) =>
  websiteHttpClient<WebsiteCreatePage>(`websites/${websiteId}/pages`, 'POST', {
    data: {
      type: pageType,
      path: url,
      name,
      locale,
    },
  })

export interface PublishWebsitePageRequest {
  websitePageId: number
}

export const publishWebsitePage = async ({
  websitePageId,
}: PublishWebsitePageRequest) =>
  websiteHttpClient<WebsitePageResource>(
    `website-pages/${websitePageId}/activate`,
    'POST',
    { data: {} },
  )

export interface UnpublishWebsitePageRequest {
  websitePageId: number
}

export const unpublishWebsitePage = async ({
  websitePageId,
}: UnpublishWebsitePageRequest) =>
  websiteHttpClient<WebsitePageResource>(
    `website-pages/${websitePageId}/deactivate`,
    'POST',
    { data: {} },
  )

export interface DeleteWebsitePageSetRequest {
  websitePageSetId: number
}
export interface DeleteWebsitePageByIdRequest {
  websitePageId: number
}

export const deleteWebsitePageSetRequest = async ({
  websitePageSetId,
}: DeleteWebsitePageSetRequest) =>
  websiteHttpClient<void>(`website-pages/${websitePageSetId}`, 'DELETE')

export const deleteWebsitePageByIdRequest = async ({
  websitePageId,
}: DeleteWebsitePageByIdRequest) =>
  websiteHttpClient<void>(`website-pages/by-page-id/${websitePageId}`, 'DELETE')

export interface AddWebsitePageLocaleRequest {
  websitePageSetId: number
  locale: PageLocaleEnum
}

export const addWebsitePageLocale = async ({
  websitePageSetId,
  locale,
}: AddWebsitePageLocaleRequest) =>
  websiteHttpClient<void>(
    `website-pages/${websitePageSetId}/add-locale`,
    'POST',
    {
      data: {
        locale,
      },
    },
  )

export interface RemoveWebsitePageLocaleRequest {
  websitePageSetId: number
  locale: PageLocaleEnum
}

export const removeWebsitePageLocale = async ({
  websitePageSetId,
  locale,
}: RemoveWebsitePageLocaleRequest) =>
  websiteHttpClient<void>(
    `website-pages/${websitePageSetId}/remove-locale`,
    'POST',
    {
      data: {
        locale,
      },
    },
  )

export interface CopyPageLocaleRequest {
  websitePageSetId: number
  locale: PageLocaleEnum
}

export const copyPageLocale = async ({
  websitePageSetId,
  locale,
}: CopyPageLocaleRequest) =>
  websiteHttpClient<void>(
    `website-pages/${websitePageSetId}/copy-page`,
    'POST',
    {
      data: {
        locale,
      },
    },
  )

export const saveWebsitePage = async (
  payload: WebsitePageSavePayloadInterface,
) =>
  pageEditorHttpClient(`page/${payload.contentPage.id}/save`, 'put', {
    data: payload,
  })
